/**
 * Cargo
 * @author Thacryba
 */
import {$, Resource, ResponseSerialize} from 'simpli-web-sdk'
import {InternationalRequest} from '@/helpers/InternationalRequest'
import {PanelUser} from '@/model/resource/PanelUser'
import {Driver} from '@/model/resource/Driver'
import {Address} from '@/model/resource/Address'
import {Occurrence} from '@/model/resource/Occurrence'
import {Receptor} from '@/model/resource/Receptor'
import {SystemItrack} from '@/model/resource/SystemItrack'
import {CargoStatus} from '@/model/resource/CargoStatus'
import {DeliveryEvent} from '@/enums/DeliveryEvent'
import {DeliveryStatus} from '@/enums/DeliveryStatus'
import {VisualizationMode} from '@/model/resource/VisualizationMode'
import moment from 'moment/moment'
import {CargoInfo} from '@/model/resource/CargoInfo'
import {BatchRemoveCargaResponse} from '@/model/response/BatchRemoveCargaResponse'

export class Cargo extends Resource {
  idCargoPk: number = 0

  @ResponseSerialize(PanelUser)
  shipper: PanelUser | null = null

  @ResponseSerialize(PanelUser)
  transporter: PanelUser | null = null

  @ResponseSerialize(PanelUser)
  creator: PanelUser | null = null

  @ResponseSerialize(Driver)
  driver: Driver | null = null

  @ResponseSerialize(Address)
  address: Address | null = null

  @ResponseSerialize(Occurrence)
  occurrence: Occurrence | null = null

  @ResponseSerialize(Receptor)
  receptor: Receptor | null = null

  @ResponseSerialize(SystemItrack)
  creatorSystem: SystemItrack | null = null

  @ResponseSerialize(SystemItrack)
  finisherSystem: SystemItrack | null = null

  @ResponseSerialize(CargoStatus)
  cargoStatus: CargoStatus | null = null

  @ResponseSerialize(VisualizationMode)
  visualizationMode: VisualizationMode | null = null

  /**
   * Folio
   */
  document: string | null = null

  emissionDate: string | null = null
  creationDate: string | null = null
  active: boolean | null = null

  note: string | null = null
  dte: string | null = null
  idDocumentPhotoFk: number | null = null
  deliveryDate: string | null = null
  systemDeliveryDate: string | null = null
  expectedDate: string | null = null

  slaStatus: boolean | null = null

  qtyOccurrencePhotos: number | null = null
  qtyReceiptsPhotos: number | null = null

  returned: boolean | null = null

  @ResponseSerialize(CargoInfo)
  cargoInfo: CargoInfo | null = null

  photosOccurrence: string[] = []
  photosReceipts: string[] = []

  urlGuest: string | null = null

  get $id() {
    return this.idCargoPk
  }
  set $id(val) {
    this.idCargoPk = val
  }
  get $tag() {
    return String(this.$id)
  }

  get idShipperFk() {
    if (!this.shipper) return 0
    return this.shipper.$id
  }
  set idShipperFk(val) {
    if (!this.shipper) this.shipper = new PanelUser()
    this.shipper.$id = val
  }

  get idDriverFk() {
    if (!this.driver) return 0
    return this.driver.$id
  }
  set idDriverFk(val) {
    if (!this.driver) this.driver = new Driver()
    this.driver.$id = val
  }

  get idReceptorFk() {
    if (!this.receptor) return 0
    return this.receptor.$id
  }
  set idReceptorFk(val) {
    if (!this.receptor) this.receptor = new Receptor()
    this.receptor.$id = val
  }

  get idAddressFk() {
    if (!this.address) return 0
    return this.address.$id
  }
  set idAddressFk(val) {
    if (!this.address) this.address = new Address()
    this.address.$id = val
  }

  get idCargoStatusFk() {
    if (!this.cargoStatus) return 0
    return this.cargoStatus.$id
  }
  set idCargoStatusFk(val) {
    if (!this.cargoStatus) this.cargoStatus = new CargoStatus()
    this.cargoStatus.$id = val
  }

  get idFinisherSystemFk() {
    if (!this.finisherSystem) return 0
    return this.finisherSystem.$id
  }
  set idFinisherSystemFk(val) {
    if (!this.finisherSystem) this.finisherSystem = new SystemItrack()
    this.finisherSystem.$id = val
  }

  get idTransporterFk() {
    if (!this.transporter) return 0
    return this.transporter.$id
  }
  set idTransporterFk(val) {
    if (!this.transporter) this.transporter = new PanelUser()
    this.transporter.$id = val
  }

  get idCreatorSystemFk() {
    if (!this.creatorSystem) return 0
    return this.creatorSystem.$id
  }
  set idCreatorSystemFk(val) {
    if (!this.creatorSystem) this.creatorSystem = new SystemItrack()
    this.creatorSystem.$id = val
  }

  get idOccurrenceFk() {
    if (!this.occurrence) return 0
    return this.occurrence.$id
  }
  set idOccurrenceFk(val) {
    if (!this.occurrence) this.occurrence = new Occurrence()
    this.occurrence.$id = val
  }

  get idVisualizationModeFk() {
    if (!this.visualizationMode) return 0
    return this.visualizationMode.$id
  }
  set idVisualizationModeFk(val) {
    if (!this.visualizationMode)
      this.visualizationMode = new VisualizationMode()
    this.visualizationMode.$id = val
  }

  get deliveryEvent() {
    if (this.occurrence && this.occurrence.$id) {
      if (this.returned) {
        return DeliveryEvent.RETURNED
      }
      return DeliveryEvent.OCCURRENCE
    }
    return DeliveryEvent.NO_OCCURRENCE
  }

  get eventTranslated() {
    if (this.cargoStatus && this.cargoStatus.$id === DeliveryStatus.PENDING) {
      return '-'
    } else if (this.deliveryEvent === DeliveryEvent.NO_OCCURRENCE) {
      return $.t('enums.DeliveryEvent.NO_OCCURRENCE')
    } else if (this.deliveryEvent === DeliveryEvent.OCCURRENCE) {
      return $.t('enums.DeliveryEvent.OCCURRENCE')
    } else if (this.deliveryEvent === DeliveryEvent.RETURNED) {
      return $.t('enums.DeliveryEvent.RETURNED')
    }
    return '-'
  }

  get eventTagClass() {
    if (this.deliveryEvent === DeliveryEvent.NO_OCCURRENCE) {
      return 'it-tag default'
    } else if (this.deliveryEvent === DeliveryEvent.OCCURRENCE) {
      return 'it-tag danger'
    } else if (this.deliveryEvent === DeliveryEvent.RETURNED) {
      return 'it-tag grapefruit'
    }
    return 'it-tag default'
  }

  get cargoStatusTagClass() {
    if (this.cargoStatus) {
      if (this.cargoStatus.$id === DeliveryStatus.PENDING) {
        return 'it-tag warning'
      } else if (this.cargoStatus.$id === DeliveryStatus.FINISHED) {
        return 'it-tag aquamarine'
      }
    }
    return 'it-tag default'
  }

  get slaTranslated() {
    const idStatus = (this.cargoStatus && this.cargoStatus.$id) || 0

    const notInformed = $.t('sla.notInformed')
    const slaIn = $.t('sla.slaIn')
    const slaOut = $.t('sla.slaOut')
    const expected = $.t('sla.expected')
    const delayed = $.t('sla.delayed')

    if (idStatus === DeliveryStatus.PENDING) {
      return this.expectedDate
        ? moment().isBefore(moment(this.expectedDate))
          ? expected
          : delayed
        : notInformed
    } else if (idStatus === DeliveryStatus.FINISHED) {
      return this.slaStatus !== null
        ? this.slaStatus
          ? slaIn
          : slaOut
        : notInformed
    }

    return ''
  }

  get slaTagClass() {
    const idStatus = (this.cargoStatus && this.cargoStatus.$id) || 0

    if (idStatus === DeliveryStatus.PENDING) {
      return this.expectedDate
        ? moment().isBefore(moment(this.expectedDate))
          ? 'it-tag success'
          : 'it-tag danger'
        : 'it-tag default'
    } else if (idStatus === DeliveryStatus.FINISHED) {
      return this.slaStatus !== null
        ? this.slaStatus
          ? 'it-tag perfume'
          : 'it-tag warning'
        : 'it-tag default'
    }

    return ''
  }

  /**
   * Deletes multiple instances of the given IDs of Carga
   */
  static async batchRemoveCargo(ids: number[]) {
    return await InternationalRequest.post(`/admin/cargo/remove-lote`, {ids})
      .name('batchRemoveCargo')
      .as(BatchRemoveCargaResponse)
      .getData()
  }

  /**
   * Deletes a instance of a given ID of Cargo
   */
  async removeCargo() {
    return await InternationalRequest.delete(`/admin/cargo/${this.$id}`)
      .name('removeCargo')
      .asNumber()
      .getData()
  }
}
